// src/telas/home/index.tsx

import styles from "./styles.module.css";
import { Menu } from "../menu";
import { useContext, useEffect, useState, useMemo } from "react";
import { PagamentoType, STATUS_PAGAMENTO } from "../../models/pagamento";
import { ContextRegister } from "../../context";
import { AuthFetchJson, url } from "../../api";
import Swal from "sweetalert2";
import { Utils } from "../../utils";
import { FaChevronUp, FaChevronDown } from "react-icons/fa";

import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
} from "@tanstack/react-table";

const columnHelper = createColumnHelper<PagamentoType>();

export function Home() {
  const [pagamentos, setPagamentos] = useState<PagamentoType[]>([]);
  const { state } = useContext(ContextRegister);

  const [dataIni, setDataIni] = useState(() => "");
  const [dataFim, setDataFim] = useState(() => "");
  const [status, setStatus] = useState(() => "0");

  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState("");

  function handleData(data: string, dataOrder: number) {
    if (dataOrder === 1) {
      setDataIni(data);
      getPagamentos(
        Utils.formatDTtoAAAAMMDD(new Date(`${data} 00:00`)),
        Utils.formatDTtoAAAAMMDD(new Date(`${dataFim} 23:59`)),
      );
    }

    if (dataOrder === 2) {
      setDataFim(data);
      getPagamentos(
        Utils.formatDTtoAAAAMMDD(new Date(`${dataIni} 00:00`)),
        Utils.formatDTtoAAAAMMDD(new Date(`${data} 23:59`)),
      );
    }
  }

  function getPagamentos(firstDate: string, lastDate: string) {
    Swal.fire({
      title: "Pagamentos",
      html: "Buscando dados de pagamentos.",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });
    const controller = new AbortController();
    const signal = controller.signal;

    AuthFetchJson(`${url}FINANCEIROPARCEIRO`, {
      signal,
      method: "POST",
      body: JSON.stringify({
        PARCEIRO: state.usuario.A2_COD,
        LOJA: state.usuario.A2_LOJA,
        DTVISITINI: firstDate,
        DTVISITFIM: lastDate,
      }),
    })
      .then((response) => {
        if (response.sucesso !== "T") {
          Swal.fire({
            title: "Pagamentos",
            html: "Erro ao buscar pagamentos. " + response.mensagem,
          });
          return;
        }
        setPagamentos(response.dados);
      })
      .catch((error) => {
        if (error.ABORT_ERR === error.code) return;
        Swal.fire({
          title: "Pagamentos",
          html: "Erro ao buscar pagamentos.",
        });
      })
      .finally(() => {
        if (Swal.isLoading()) Swal.close();
      });

    return controller;
  }

  useEffect(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();

    const firstDate = new Date(year, month, 1, 0, 0);
    const lastDate = new Date(year, month + 1, 0, 0, 0);

    setDataIni(firstDate.toISOString().slice(0, 10));
    setDataFim(lastDate.toISOString().slice(0, 10));

    const controller = getPagamentos(
      Utils.formatDTtoAAAAMMDD(firstDate),
      Utils.formatDTtoAAAAMMDD(lastDate),
    );

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filtragem por status feita via dado filtrado (mantém lógica original)
  const dadosFiltrados = useMemo(() => {
    if (status === "0") return pagamentos;
    return pagamentos.filter((pg) => pg.STATUS === status);
  }, [pagamentos, status]);

  const totalGeral = useMemo(
    () => pagamentos.reduce((acum, value) => acum + value.VRTOTAL, 0),
    [pagamentos],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor("A1_NOME", {
        header: "Nome",
      }),
      columnHelper.accessor("B1_DESC", {
        header: "Descrição",
      }),
      columnHelper.accessor("STATUS", {
        header: "Status",
        cell: (info) => STATUS_PAGAMENTO[info.getValue()],
        size: 110,
      }),
      columnHelper.accessor("Z1_DTAGEND", {
        header: "Data Agenda",
        cell: (info) => Utils.formatDtProtheus(info.getValue()),
        size: 110,
      }),
      columnHelper.accessor("Z1_DTVISIT", {
        header: "Data Visita",
        cell: (info) => Utils.formatDtProtheus(info.getValue()),
        size: 110,
      }),
      columnHelper.accessor("VENCIMENTO", {
        header: "Vencimento",
        cell: (info) => Utils.formatDtProtheus(info.getValue()),
        size: 110,
      }),
      columnHelper.accessor("PAGAMENTO", {
        header: "Pagamento",
        cell: (info) => Utils.formatDtProtheus(info.getValue()),
        size: 110,
      }),
      columnHelper.accessor("VRTOTAL", {
        header: "Valor",
        cell: (info) => Utils.formaterCurrency.format(info.getValue()),
        size: 110,
      }),
    ],
    [],
  );

  const table = useReactTable({
    data: dadosFiltrados,
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <>
      <Menu />
      <div className={styles.container}>
        <div className={styles.content}>
          <h2>Pagamentos</h2>

          {/* Filtros */}
          <div className={styles.filterContainer}>
            <div className={styles.statusContainer}>
              <label>Status</label>
              <select onChange={(event) => setStatus(event.target.value)}>
                <option value={0}>Todos</option>
                {Object.keys(STATUS_PAGAMENTO).map((v) => (
                  <option key={`${v}opt`} value={v}>
                    {STATUS_PAGAMENTO[v]}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.dataContainer}>
              <label>Data Inicial e Final (Visita)</label>
              <input
                value={dataIni}
                onChange={(event) => handleData(event.target.value, 1)}
                type="date"
                placeholder="data inicial"
              />
              <input
                value={dataFim}
                onChange={(event) => handleData(event.target.value, 2)}
                type="date"
                placeholder="data final"
              />
            </div>
          </div>

          {/* Toolbar: busca + contador */}
          <div className={styles.tableToolbar}>
            <input
              className={styles.searchInput}
              placeholder="Buscar pagamento..."
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
            />
            <span className={styles.countLabel}>
              {table.getFilteredRowModel().rows.length} registro(s)
            </span>
          </div>

          {/* Tabela */}
          <table className={styles.table}>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      style={{ width: header.getSize() }}
                      onClick={header.column.getToggleSortingHandler()}
                      className={
                        header.column.getCanSort() ? styles.sortable : ""
                      }
                    >
                      <span className={styles.thInner}>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        {header.column.getIsSorted() === "asc" && (
                          <FaChevronUp className={styles.sortIcon} />
                        )}
                        {header.column.getIsSorted() === "desc" && (
                          <FaChevronDown className={styles.sortIcon} />
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className={styles.emptyRow}>
                    Nenhum pagamento encontrado.
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={columns.length - 1}></td>
                <td>{Utils.formaterCurrency.format(totalGeral)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </>
  );
}
